import { useState, useEffect, useCallback, useContext } from 'react';
import { Student, Location, Path, StudentFormData, StudentPosition } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { calculateStudentPosition } from '../utils/pathUtils';
import { hallPassApiService, MapDetails } from '../services/hallPassApiService';
import { UserContext } from '../contexts/UserContext';
import { useHallPassTypes } from '../contexts/HallPassTypesContext';
import { calculateExpectedReturnTime } from '../utils/timeUtils';
import { useHallPasses } from '../contexts/HallPassesContext';
import mediaService from '../services/mediaService';

export const DEFAULT_MAP_IMAGE = 'https://i.imgur.com/4zu4j6p.png';

// Initial data for demo purposes
const initialLocations: Location[] = [
  { id: 'building1', name: 'Main Building', type: 'building', position: { x: 268, y: 1056 } },
  { id: 'building2', name: 'Science Wing', type: 'building', position: { x: 602, y: 362 } },
  { id: 'classroom1', name: 'Potions Class', type: 'classroom', position: { x: 268, y: 1056 }, buildingId: 'building1' },
  { id: 'classroom2', name: 'Transfiguration', type: 'classroom', position: { x: 602, y: 362 }, buildingId: 'building2' },
  { id: 'classroom3', name: 'Defense Against Dark Arts', type: 'classroom', position: { x: 662, y: 717 }, buildingId: 'building2' },
  { id: 'restroom1', name: 'First Floor Bathroom', type: 'restroom', position: { x: 271, y: 558 } },
  { id: 'restroom2', name: 'Second Floor Bathroom', type: 'restroom', position: { x: 271, y: 205 } },
];

const initialPaths: Path[] = [
  {
    id: 'path1',
    startLocationId: 'building1',
    endLocationId: 'restroom1',
    waypoints: [{ x: 268, y: 800 }, { x: 271, y: 700 }]
  },
  {
    id: 'path2',
    startLocationId: 'building2',
    endLocationId: 'restroom1',
    waypoints: [{ x: 450, y: 460 }, { x: 350, y: 558 }]
  },
  {
    id: 'path3',
    startLocationId: 'building2',
    endLocationId: 'restroom2',
    waypoints: [{ x: 450, y: 300 }, { x: 350, y: 205 }]
  },
  {
    id: 'path4',
    startLocationId: 'classroom3',
    endLocationId: 'restroom2',
    waypoints: [{ x: 662, y: 500 }, { x: 450, y: 205 }]
  },
];

export const useStudentData = () => {
  const { userProfile } = useContext(UserContext);
  const { hallPassTypes } = useHallPassTypes();
  const { activeHallPasses, removeFromHub } = useHallPasses();
  const [students, setStudents] = useState<Student[]>([]);
  // Local-only demo/extra students not coming from real hall passes
  const [extraStudents, setExtraStudents] = useState<Student[]>([]);
  const [locations, setLocations] = useState<Location[]>(initialLocations);
  const [mapImage, setMapImage] = useState<string>(DEFAULT_MAP_IMAGE);
  const [paths, setPaths] = useState<Path[]>(initialPaths);
  const [studentPositions, setStudentPositions] = useState<Record<string, StudentPosition>>({});
  const [editingMap, setEditingMap] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentMapId, setCurrentMapId] = useState<string | null>(null);

  // Recalculate student positions based on current time and paths
  const recalcStudentPositions = useCallback(() => {
    const now = Date.now();
    const newPositions: Record<string, StudentPosition> = {};

    students.forEach(student => {
      const path = paths.find(p => p.id === student.pathId);
      if (path) {
        newPositions[student.userId] = calculateStudentPosition(
          student,
          path,
          locations,
          now
        );
      }
    });

    setStudentPositions(newPositions);
  }, [students, paths, locations]);

  // Helper function to get organization's default map ID
  const getMapId = useCallback(async (): Promise<string> => {
    if (currentMapId) {
      return currentMapId;
    }

    if (!userProfile?.orgId) {
      throw new Error('No organization ID available');
    }

    try {
      const { mapId } = await hallPassApiService.getOrCreateDefaultMap(userProfile.orgId);
      setCurrentMapId(mapId);
      return mapId;
    } catch (error) {
      console.error('Failed to get map ID:', error);
      throw error;
    }
  }, [currentMapId, userProfile?.orgId]);

  // Load initial map data from API (hall passes will come from context)
  useEffect(() => {
    const loadInitialData = async () => {
      if (!userProfile?.orgId) {
        // stay in demo mode until the user profile loads
        setLocations(initialLocations);
        setPaths(initialPaths);
        return;
      }

      setLoading(true);
      try {
        // Initialize the map ID for this organization
        let mapId: string | null = null;
        try {
          mapId = await getMapId();
        } catch (error) {
          console.warn('Failed to initialize map ID:', error);
        }

        // Try to load map data if endpoints exist
        try {
          let map: MapDetails | undefined;
          if (mapId) {
            map = await hallPassApiService.getMap(mapId);
          }
          if (map) {
            if (map.backgroundUrl) {
              const raw = String(map.backgroundUrl);
              const isAbsolute = /^https?:\/\//i.test(raw);
              const resolved = isAbsolute
                ? raw
                : (userProfile?.orgId
                    ? mediaService.getDirectUrl(userProfile.orgId, raw)
                    : mediaService.toAbsoluteUrl(raw));
              setMapImage(resolved);
            }

            const mapHasLocations = Array.isArray(map.locations) && map.locations.length > 0;
            const mapHasPaths = Array.isArray(map.paths) && map.paths.length > 0;

            if (mapHasLocations || mapHasPaths) {
              const locationData = (map.locations || []).map(loc => hallPassApiService.convertApiLocationToLocal(loc));
              const pathData = (map.paths || []).map(path => hallPassApiService.convertApiPathToLocal(path));
              if (locationData.length) setLocations(locationData);
              if (pathData.length) setPaths(pathData);
            } else {
              // Backend often stores geometry in /api/MapLocations + /api/MapPaths, not embedded on /api/Maps/{id}
              const mapLocations = mapId
                ? await hallPassApiService.getMapLocations(mapId)
                : await hallPassApiService.getMapLocations();
              const locationData = mapLocations.map(loc => hallPassApiService.convertApiLocationToLocal(loc));
              if (locationData.length) setLocations(locationData);

              const mapPaths = mapId
                ? await hallPassApiService.getMapPaths(mapId)
                : await hallPassApiService.getMapPaths();
              const pathData = mapPaths.map(path => hallPassApiService.convertApiPathToLocal(path));
              if (pathData.length) setPaths(pathData);
            }
          } else {
            // fallback to individual endpoints
            const mapLocations = mapId
              ? await hallPassApiService.getMapLocations(mapId)
              : await hallPassApiService.getMapLocations();
            const locationData = mapLocations.map(loc => hallPassApiService.convertApiLocationToLocal(loc));
            setLocations(locationData);

            const mapPaths = mapId
              ? await hallPassApiService.getMapPaths(mapId)
              : await hallPassApiService.getMapPaths();
            const pathData = mapPaths.map(path => hallPassApiService.convertApiPathToLocal(path));
            setPaths(pathData);
          }
        } catch (error) {
          console.warn('Failed to load map data, using initial data');
          setLocations(initialLocations);
          setPaths(initialPaths);
        }

      } catch (error) {
        console.error('Failed to load hall pass data:', error);
        // Fall back to demo data
        setLocations(initialLocations);
        setPaths(initialPaths);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [userProfile?.orgId, getMapId]);

  // Derive students from active hall passes in context and merge with any local extras
  useEffect(() => {
    const normalizeLocationType = (locationType: any): Location['type'] => {
      const t = String(locationType || '').toLowerCase();
      if (t === 'classroom' || t === 'restroom' || t === 'building' || t === 'other') return t;
      return 'other';
    };

    const toLocation = (apiLocation: any): Location | null => {
      if (!apiLocation?.locationId) return null;
      const x = Number(apiLocation.posX);
      const y = Number(apiLocation.posY);
      return {
        id: String(apiLocation.locationId),
        name: apiLocation.name || 'Unknown',
        type: normalizeLocationType(apiLocation.locationType),
        position: {
          x: Number.isFinite(x) ? x : 0,
          y: Number.isFinite(y) ? y : 0,
        },
        ...(normalizeLocationType(apiLocation.locationType) === 'classroom' && {
          buildingId: apiLocation.parentBuildingId ? String(apiLocation.parentBuildingId) : undefined,
        }),
      };
    };

    const toPath = (apiPath: any): Path | null => {
      if (!apiPath?.pathId) return null;
      const startLocationId = apiPath.startLocationId ? String(apiPath.startLocationId) : '';
      const endLocationId = apiPath.endLocationId ? String(apiPath.endLocationId) : '';
      if (!startLocationId || !endLocationId) return null;
      const waypoints = typeof apiPath.waypoints === 'string'
        ? hallPassApiService.parseWaypoints(apiPath.waypoints)
        : Array.isArray(apiPath.waypoints)
          ? apiPath.waypoints
          : [];
      return {
        id: String(apiPath.pathId),
        startLocationId,
        endLocationId,
        waypoints,
      };
    };

    // Map active hall passes to Student records for display/animation
    const baseStudents: Student[] = (activeHallPasses || []).map((hp: any) => {
      const type = hallPassTypes.find(t => t.hallPassTypeId === hp.hallPassTypeId);

      const startLocationId = hp.startLocation?.locationId || type?.startLocationDetails?.locationId || '';
      const destinationId = hp.destinationLocation?.locationId || type?.destinationLocationDetails?.locationId || '';
      const destinationName = hp.destinationLocation?.name || type?.destinationLocationDetails?.name || type?.hallPassTypeDescription || '';
      const pathId = hp.explicitPath?.pathId || hp.hallPassTypePath?.pathId || type?.pathId || '';

      const rawStart = hp.requestDecisionedAt || hp.requestAt || hp.createdAt || hp.startTime;
      const startTime = new Date(rawStart || Date.now()).getTime();
      const duration = Number(hp.durationMinutes ?? type?.hallPassTypeDuration ?? 0);

      const studentProfile = (userProfile?.students || []).find((s: any) => s.userId === hp.studentId);
      const teacherProfile = (userProfile?.teachers || []).find((t: any) => t.userId === hp.teacherId || t.id === hp.teacherId);

      return {
        userId: hp.hallPassId || hp.id,
        name: studentProfile?.name || hp.studentName || `Student ${hp.studentId}`,
        avatar: studentProfile?.avatar,
        startTime,
        expectedReturnTime: startTime + duration * 60 * 1000,
        startLocationId,
        destinationId,
        destinationName,
        pathId,
        returned: !!hp.returnAt,
        teacherId: hp.teacherId,
        teacherName: teacherProfile?.name,
      } as Student;
    });

    // Ensure the map has any location/path geometry referenced by active passes/types.
    // This prevents a blank map when /api/Maps/{id} returns no embedded locations/paths.
    const derivedLocations: Location[] = [];
    const derivedPaths: Path[] = [];

    (activeHallPasses || []).forEach((hp: any) => {
      const type = hallPassTypes.find(t => t.hallPassTypeId === hp.hallPassTypeId);

      const locCandidates = [
        hp.startLocation,
        hp.destinationLocation,
        hp.explicitStartLocation,
        hp.explicitDestinationLocation,
        type?.startLocationDetails,
        type?.destinationLocationDetails,
      ];

      locCandidates.forEach((loc) => {
        const converted = toLocation(loc);
        if (converted) derivedLocations.push(converted);
      });

      const pathCandidates = [
        hp.explicitPath,
        hp.hallPassTypePath,
        type?.path,
      ];

      pathCandidates.forEach((p) => {
        const converted = toPath(p);
        if (converted) derivedPaths.push(converted);
      });
    });

    if (derivedLocations.length) {
      setLocations((prev) => {
        const byId = new Map(prev.map(l => [l.id, l] as const));
        derivedLocations.forEach((l) => {
          if (!byId.has(l.id)) byId.set(l.id, l);
        });
        return Array.from(byId.values());
      });
    }

    if (derivedPaths.length) {
      setPaths((prev) => {
        const byId = new Map(prev.map(p => [p.id, p] as const));
        derivedPaths.forEach((p) => {
          if (!byId.has(p.id)) byId.set(p.id, p);
        });
        return Array.from(byId.values());
      });
    }

    // Merge extras and base (prefer base for duplicates by userId)
    const mergedMap = new Map<string, Student>();
    extraStudents.forEach(s => mergedMap.set(s.userId, s));
    baseStudents.forEach(s => mergedMap.set(s.userId, s));
    setStudents(Array.from(mergedMap.values()));
  }, [activeHallPasses, hallPassTypes, userProfile, extraStudents]);

  // Update student positions regularly for animations
  useEffect(() => {
    // Calculate immediately in case students already exist
    recalcStudentPositions();
    const interval = setInterval(recalcStudentPositions, 100);
    return () => clearInterval(interval);
  }, [recalcStudentPositions]);

  // Remove global event listener; updates come from HallPassesContext activeHallPasses
  // Add a new student with hall pass
  const addStudent = useCallback(async (data: StudentFormData) => {
    // Find the classroom and its building
    const classroom = locations.find(loc => loc.id === data.startLocationId);
    if (!classroom?.buildingId) return;

    const building = locations.find(loc => loc.id === classroom.buildingId);
    if (!building) return;

    // Find appropriate path using building location
    const path = paths.find(
      p => p.startLocationId === building.id && p.endLocationId === data.destinationId
    );

    if (!path) return;

    const destinationLocation = locations.find(loc => loc.id === data.destinationId);
    if (!destinationLocation) return;

    const now = Date.now();
    const expectedReturnTime = calculateExpectedReturnTime(now, destinationLocation.type);

    const newStudent: Student = {
      userId: uuidv4(),
      name: data.name,
      startTime: now,
      expectedReturnTime,
      startLocationId: data.startLocationId,
      destinationId: data.destinationId,
      pathId: path.id,
      returned: false,
    };
  setExtraStudents(prev => [...prev, newStudent]);
    // ensure their position is calculated immediately
    recalcStudentPositions();

  }, [locations, paths, userProfile, recalcStudentPositions]);
  // Mark a student as returned
  const markStudentReturned = useCallback(async (studentId: string) => {
  const student = students.find(s => s.userId === studentId);
    if (!student) return;
    const role = userProfile?.mainRole || '';
    const currentUserId = userProfile?.userId;
    const canEnd = (() => {
      if (role.includes('CAMPUSSECURITYOFFICER')) return true;
      if (role.includes('ACORNLEAD')) return true;
      if (role.includes('TEACHER') && student.teacherId && student.teacherId === currentUserId) return true;
      return false;
    })();
    if (!canEnd) {
      console.warn('User not authorized to end this hall pass');
      return;
    }
    try {
      // If this is a demo/extra student, just remove locally
      const isExtra = extraStudents.some(s => s.userId === studentId);
      if (isExtra) {
        setExtraStudents(prev => prev.filter(s => s.userId !== studentId));
        return;
      }
      await hallPassApiService.returnHallPass(studentId);
      // Optimistically remove from context so map updates immediately
      removeFromHub({ hallPassId: studentId });
    } catch (error) {
      console.error('Failed to return hall pass:', error);
    }
  }, [students, userProfile, extraStudents, removeFromHub]);

  // Remove a student from tracking
  const removeStudent = useCallback((studentId: string) => {
    // Only remove from extras; real hall pass removals are driven by context/API
    setExtraStudents(prev => prev.filter(student => student.userId !== studentId));
  }, []);
  // Add a new location via API
  const addLocation = useCallback(async (location: Omit<Location, 'id'>): Promise<string> => {
    if (!userProfile?.orgId) {
      // Fall back to local creation if no org context
      const newLocation: Location = { ...location, id: uuidv4() };
      setLocations(prev => [...prev, newLocation]);
      return newLocation.id;
    }
    try {
      // Get the map ID for the current organization
      const mapId = await getMapId();

      const newLocation = await hallPassApiService.createMapLocation({
        mapId,
        organizationId: userProfile.orgId,
        name: location.name,
        locationType: location.type,
        posX: location.position.x,
        posY: location.position.y,
        parentBuildingId: location.buildingId
      });

      const localLocation = hallPassApiService.convertApiLocationToLocal(newLocation);
      setLocations(prev => [...prev, localLocation]);
      return localLocation.id;
    } catch (error) {
      console.error('Failed to create location:', error);
      // Fall back to local creation
      const newLocation: Location = { ...location, id: uuidv4() };
      setLocations(prev => [...prev, newLocation]);
      return newLocation.id;
    }
  }, [userProfile]);

  // Update location position
  const updateLocationPosition = useCallback((locationId: string, position: { x: number; y: number }) => {
    setLocations(prev =>
      prev.map(loc =>
        loc.id === locationId
          ? { ...loc, position }
          : loc
      )
    );
  }, []);  // Update location details via API
  const updateLocation = useCallback(async (location: Location) => {
    try {      // Ensure we have a valid GUID format for mapId
      const mapId = await getMapId();
      console.log('Updating location with mapId:', mapId, 'and data:', location);

      // Ensure locationId is a valid GUID format
      let locationId = location.id;
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!guidRegex.test(locationId)) {
        console.warn('Location ID is not a valid GUID, this might cause issues:', locationId);
      }

      // Create a complete MapLocation object for the API
      const updateData = {
        locationId: locationId,
        mapId: mapId,
        organizationId: userProfile?.orgId || '',
        name: location.name,
        locationType: location.type,
        posX: Number(location.position.x),
        posY: Number(location.position.y),
        parentBuildingId: location.buildingId || null
      };

      console.log('Sending update data to API:', updateData);
      await hallPassApiService.updateMapLocation(location.id, updateData);

      setLocations(prev =>
        prev.map(loc =>
          loc.id === location.id
            ? { ...location }
            : loc
        )
      );
    } catch (error) {
      console.error('Failed to update location:', error);
      // Fall back to local update
      setLocations(prev =>
        prev.map(loc =>
          loc.id === location.id
            ? { ...location }
            : loc)
      );
    }
  }, [userProfile, getMapId]);
  // Delete a location via API
  const deleteLocation = useCallback(async (locationId: string) => {
    try {
      // Check if there are any paths that use this location
      const relatedPathIds = paths.filter(
        path => path.startLocationId === locationId || path.endLocationId === locationId
      ).map(path => path.id);

      // Call API to delete the location
      await hallPassApiService.deleteMapLocation(locationId);

      // Remove the location locally
      setLocations(prev => prev.filter(loc => loc.id !== locationId));

      // Delete related paths (do this after updating locations to avoid circular dependency)
      setPaths(prev => prev.filter(path => !relatedPathIds.includes(path.id)));

      // Also update any students using these paths to be returned
      setStudents(prev =>
        prev.map(student =>
          relatedPathIds.includes(student.pathId) && !student.returned
            ? { ...student, returned: true }
            : student
        )
      );
    } catch (error) {
      console.error('Failed to delete location:', error);
      // For delete operations, we might want to show an error but not do local fallback
      // since the user expects the item to be deleted
      throw error; // Re-throw to let the UI handle the error
    }
  }, [paths]);  // Add a new path via API
  const addPath = useCallback(async (startLocationId: string, endLocationId: string, waypoints: { x: number; y: number }[]) => {
    try {
      // Get the map ID for the current organization
      const mapId = await getMapId();

      const newPath = await hallPassApiService.createMapPath({
        mapId,
        startLocationId,
        endLocationId,
        waypoints
      });

      const localPath = hallPassApiService.convertApiPathToLocal(newPath);
      setPaths(prev => [...prev, localPath]);
      return localPath.id;
    } catch (error) {
      console.error('Failed to create path:', error);
      // Fall back to local creation
      const newPath: Path = {
        id: uuidv4(),
        startLocationId,
        endLocationId,
        waypoints,
      };

      setPaths(prev => [...prev, newPath]); return newPath.id;
    }
  }, [getMapId]);
  // Update path waypoints via API
  const updatePathWaypoints = useCallback(async (pathId: string, waypoints: { x: number; y: number }[]) => {
    try {
      await hallPassApiService.updateMapPath(pathId, { waypoints });

      setPaths(prev =>
        prev.map(path =>
          path.id === pathId
            ? { ...path, waypoints }
            : path
        )
      );
    } catch (error) {
      console.error('Failed to update path waypoints:', error);
      // Fall back to local update
      setPaths(prev =>
        prev.map(path =>
          path.id === pathId
            ? { ...path, waypoints }
            : path
        )
      );
    }
  }, []);
  // Delete a path via API
  const deletePath = useCallback(async (pathId: string) => {
    try {
      await hallPassApiService.deleteMapPath(pathId);

      setPaths(prev => prev.filter(path => path.id !== pathId));

      // Also update any students using this path to be returned
      setStudents(prev =>
        prev.map(student =>
          student.pathId === pathId && !student.returned
            ? { ...student, returned: true }
            : student
        )
      );
    } catch (error) {
      console.error('Failed to delete path:', error);
      // Fall back to local deletion
      setPaths(prev => prev.filter(path => path.id !== pathId));

      // Also update any students using this path to be returned
      setStudents(prev =>
        prev.map(student =>
          student.pathId === pathId && !student.returned
            ? { ...student, returned: true }
            : student
        )
      );
    }
  }, []);

  // Get active and overdue students
  const getActiveStudents = useCallback(() => {
    return students.filter(student => !student.returned);
  }, [students]);

  const getOverdueStudents = useCallback(() => {
    const now = Date.now();
    return students.filter(student =>
      !student.returned && student.expectedReturnTime < now
    );
  }, [students]);
  // Save map configuration - removed localStorage usage
  const saveMapConfig = useCallback(() => {
    // Map configuration is now managed by the API
    console.log('Map configuration saved to server');
  }, []);

  // Load map configuration from API instead of localStorage
  useEffect(() => {
    // Map configuration will be loaded from API calls
    // No longer using localStorage for map config
  }, []);
  return {
    students,
    locations,
    paths,
    studentPositions,
    mapImage,
    setMapImage,
    editingMap,
    setEditingMap,
    loading,
    addStudent,
    markStudentReturned,
    removeStudent,
    addLocation,
    updateLocationPosition,
    updateLocation,
    deleteLocation,
    addPath,
    updatePathWaypoints,
    deletePath,
    getActiveStudents,
    getOverdueStudents,
    saveMapConfig,
  };
};